import { createPublicClient, createWalletClient, http, type Account, type Address, type Chain, type Hex, type PublicClient, type Transport, type WalletClient } from 'viem';
import { privateKeyToAccount } from 'viem/accounts';
import { foundry } from 'viem/chains';
import * as abis from './abis.js';
export * from './abis.js';
export * from './actions.js';
export * from './openvrf.js';
export * from './batching.js';
export { abis };

/** Shape of contracts/deployments/<network>.json written by the deployment scripts. */
export interface RunnerDeployment {
  network: string;
  chainId: number;
  warning?: string;
  deployer: Address;
  keeper: Address;
  poolManager: Address;
  v4Quoter: Address;
  stateView: Address;
  runnerToken: Address;
  quoteAsset: Address;
  hook: Address;
  hookSalt: Hex;
  registry: Address;
  router: Address;
  treasury: Address;
  rewards: Address;
  prizes: Address;
  buyback: Address;
  game: Address;
  governance: Address;
  randomnessProvider: Address;
  randomnessAdapter: Address;
  liquidityLock: Address;
  poolId: Hex;
  currency0: Address;
  currency1: Address;
  lpFee: number;
  tickSpacing: number;
  /** The hook fee at deployment. Governance may change it (D61): read `RunnerHook.feeBps()` for the current fee. */
  hookFeeBps: number;
  /** Highest fee governance may set (D61), fixed in the hook at deployment; absent in manifests written before D61. */
  maxHookFeeBps?: number;
  /** Anti-sniping launch fee on buys (D57): bps at genesis, falling to hookFeeBps over launchFeeSeconds; absent = none. */
  launchFeeBps?: number;
  launchFeeSeconds?: number;
  genesis: number;
  windowSeconds: number;
  epochSeconds: number;
  quoteDecimals: number;
  referenceSupply: string;
  deployedAtBlock: number;
  /** Change order 4.0: immutable game engines registered at deployment (engine v1 is pinned by config 1). */
  engineV1?: Address;
  engineV2?: Address;
  /** Runner identity collection (ERC-721), its immutable base renderer and the owner-keyed wardrobe. */
  identity: Address;
  identityRenderer: Address;
  wardrobe: Address;
  /** Fixed backing B per identity (raw RUNNER units; forge writes large integers as decimal strings). */
  identityBacking: string | number;
  identityCap: number;
  /** LOCAL/TEST economic profile id DeployLocal used (`RUNNER_ECON_PROFILE`, contracts/script/EconomicProfiles.sol).
   *  Absent in manifests written before profiles existed (those deployments used DefaultConfig = FIXTURE_V1). Never a
   *  production approval. */
  economicProfile?: string;
  /** OpenVRF router the plan deployed itself (params.randomness.mode "openvrf-self", D54); absent otherwise. */
  openVrfRouter?: Address;
}

/**
 * Anvil's well-known development accounts (mnemonic "test test ... junk"). DISPOSABLE LOCAL KEYS ONLY:
 * they are public knowledge and must never hold real funds or be used on any other chain.
 */
export const ANVIL_DEV_KEYS: readonly Hex[] = [
  '0xac0974bec39a17e36ba4a6b4d238ff944bacb478cbed5efcae784d7bf4f2ff80',
  '0x59c6995e998f97a5a0044966f0945389dc9e86dae88c7a8412f4603b6b78690d',
  '0x5de4111afa1a4b94908f83103eb1f1706367c2e68ca870fc3fb9a804cdab365a',
  '0x7c852118294e51e653712a81e05800f419141751be58f605c371e15141b007a6',
  '0x47e179ec197488593b187f80a00eb0da91f1b9d0b13f8733639f19c30a34926a',
  '0x8b3a350cf5c34c9194ca85829a2df0ec3153be0318b5e2d3348e872092edffba',
];

export const LOCAL_CHAIN: Chain = { ...foundry, id: 31337, name: 'Anvil (local)' };

export function localClients(rpcUrl = 'http://127.0.0.1:8545') {
  const publicClient = createPublicClient({ chain: LOCAL_CHAIN, transport: http(rpcUrl) });
  const wallet = (index: number): WalletClient<Transport, Chain, Account> => {
    const key = ANVIL_DEV_KEYS[index];
    if (!key) throw new Error(`no dev key ${index}`);
    return createWalletClient({ account: privateKeyToAccount(key), chain: LOCAL_CHAIN, transport: http(rpcUrl) });
  };
  return { publicClient: publicClient as PublicClient, wallet };
}

export const RunStatus = ['None', 'CourseRequested', 'Active', 'Finished'] as const;
export const ShoeStatus = ['None', 'Open', 'Sealed', 'Collected', 'Missed', 'Drawing', 'Claimable', 'Unawarded'] as const;
export const ProposalStatus = ['None', 'Draft', 'Simulated', 'Reviewed', 'Scheduled', 'Active', 'Retired', 'Cancelled'] as const;
export const ProposalKind = [
  'RegisterFormation',
  'RetireFormation',
  'RegisterConfig',
  'ActivateConfig',
  'SetOpsConfig',
  'SetOpsRecipient',
  'SetBuybackLimits',
  'SetBuybackKeeper',
  'SetDelay',
  'RecordCodeRelease',
  'ScheduleWaterfallPolicy',
  'RegisterEngine',
  'SetIdentityRenderer',
  'RegisterCosmeticItem',
  'SetIdentityIntake',
  'SetHookFee',
] as const;

/**
 * Release class of a governance proposal kind. Every kind currently waits the same delay (RunnerGovernance has no
 * separate cosmetic policy); the class says what a release can change:
 *  - economic: money, game rules, weights or intake (waterfall policy, engines, configurations, formations, operations,
 *    buyback, governance delay, identity intake, the hook fee)
 *  - cosmetic: presentation only (identity renderer, wardrobe items) — never weight, steering, hitboxes, odds or backing
 *  - record: an on-chain record of a code identity; activation is a separate deployment
 */
export type ReleaseClass = 'economic' | 'cosmetic' | 'record';
export function proposalKindClass(kind: number | string): ReleaseClass {
  const name = typeof kind === 'number' ? ProposalKind[kind] : kind;
  if (name === 'SetIdentityRenderer' || name === 'RegisterCosmeticItem') return 'cosmetic';
  if (name === 'RecordCodeRelease') return 'record';
  return 'economic';
}

/** RunnerGame.RoadEffect `effect` codes (index = code). */
export const RoadEffectName = ['none', 'water', 'overheat', 'exit-holders', 'exit-forge'] as const;
/** RunnerGame.ModifierEnded `reason` codes (index = code). */
export const ModifierEndReasonName = ['none', 'steps', 'cap', 'water', 'run-ended'] as const;
/** RunnerTypes.Route (index = code). */
export const RouteName = ['neutral', 'forge', 'harvest'] as const;
/** RunnerTreasury donation buckets (code → name). Donations are tagged sponsorship, never fee revenue. */
export const DonationBucket: Record<number, 'prize' | 'forge' | 'road' | 'operations'> = { 1: 'prize', 2: 'forge', 3: 'road', 4: 'operations' };
/** RunnerWardrobe slots (index = slot). */
export const WardrobeSlot = ['shoes', 'jacket', 'bib', 'animation'] as const;
/** RunnerRewards.IdentityMinted `source` (index = code). */
export const IdentitySource = ['deposit', 'convert'] as const;

/** Convert a bigint-heavy object into JSON-safe decimal strings (API large integers are decimal strings). */
export function toJsonSafe<T>(v: T): unknown {
  if (typeof v === 'bigint') return v.toString();
  if (Array.isArray(v)) return v.map(toJsonSafe);
  if (v && typeof v === 'object') {
    const o: Record<string, unknown> = {};
    for (const [k, x] of Object.entries(v as Record<string, unknown>)) o[k] = toJsonSafe(x);
    return o;
  }
  return v;
}

export function windowOf(d: Pick<RunnerDeployment, 'genesis' | 'windowSeconds'>, t: number | bigint): number {
  const tt = Number(t);
  return tt < d.genesis ? 0 : Math.floor((tt - d.genesis) / d.windowSeconds);
}

export function windowStart(d: Pick<RunnerDeployment, 'genesis' | 'windowSeconds'>, w: number): number {
  return d.genesis + w * d.windowSeconds;
}

export function epochOf(d: Pick<RunnerDeployment, 'genesis' | 'epochSeconds'>, t: number | bigint): number {
  const tt = Number(t);
  return tt < d.genesis ? 0 : Math.floor((tt - d.genesis) / d.epochSeconds);
}
